import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from 'react-router-dom';
import { AppShell } from './AppShell';
import { useSession } from '@/lib/session';
import { LoadingState, PermissionDenied } from '@/design/states';
import type { Permission } from '@/lib/permissions';

/**
 * Route composition.
 *
 * Each epic owns exactly one file - `src/features/<epic>/routes.tsx` - and
 * contributes a `RouteObject[]`. This file lists them once. That is the whole
 * point: eleven feature branches adding screens touch eleven separate files and
 * one line each here, instead of all rewriting a single route tree.
 *
 * Every feature module is lazy. The login screen must not download the pharmacy
 * inventory grid, and a receptionist should never pay for the radiology report
 * editor's bundle.
 */

const authRoutes = () => import('@/features/auth/routes');
const orgRoutes = () => import('@/features/org/routes');
const patientRoutes = () => import('@/features/patients/routes');
const appointmentRoutes = () => import('@/features/appointments/routes');
const flowRoutes = () => import('@/features/flow/routes');
const visitRoutes = () => import('@/features/visits/routes');
const labRoutes = () => import('@/features/lab/routes');
const imagingRoutes = () => import('@/features/imaging/routes');
const diagnosticCaseRoutes = () => import('@/features/diagnostic-cases/routes');
const pharmacyRoutes = () => import('@/features/pharmacy/routes');
const financeRoutes = () => import('@/features/finance/routes');
const staffRoutes = () => import('@/features/staff/routes');
const platformRoutes = () => import('@/features/platform/routes');
const referenceRoutes = () => import('@/features/reference/routes');
const devRoutes = () => import('@/features/dev/routes');

/**
 * Guards an authenticated branch of the tree.
 *
 * The permission check here is a usability control: it stops a user navigating
 * into a screen whose every request would 403. The server checks again on each
 * of those requests, and that check is the one that matters (RBAC-004).
 */
export function RequireAuth({ permission }: { permission?: Permission }) {
  const { user, loading, sessionError } = useSession();

  if (loading) return <LoadingState />;
  // A refused session is NOT a missing one. Redirecting here sent a user whose
  // credentials had just been accepted straight back to the login form, with
  // nothing shown and nothing logged - which is how a platform operator with no
  // clinic membership appeared to be unable to log in at all.
  if (sessionError === 'forbidden') return <PermissionDenied />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (permission && !user.permissions.includes(permission)) return <PermissionDenied />;

  return <Outlet />;
}

/** Platform-operator surfaces. `isPlatformAdmin` is the only cross-tenant role. */
export function RequirePlatformAdmin() {
  const { user, loading, sessionError } = useSession();
  if (loading) return <LoadingState />;
  if (sessionError === 'forbidden') return <PermissionDenied contactRole="فريق المنصة" />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.isPlatformAdmin) return <PermissionDenied contactRole="فريق المنصة" />;
  return <Outlet />;
}

/** Wraps authenticated screens in the shell. */
function ShellLayout() {
  return (
    <AppShell>
      <Suspense fallback={<LoadingState />}>
        <Outlet />
      </Suspense>
    </AppShell>
  );
}

/** Full-bleed screens: auth, onboarding, public booking, TV mode. */
function BareLayout() {
  return (
    <Suspense fallback={<LoadingState />}>
      <Outlet />
    </Suspense>
  );
}

const NotFound = lazy(() => import('@/features/dev/NotFound'));

/**
 * Landing route.
 *
 * Sends each role to the board they actually start their day on, rather than a
 * generic dashboard nobody uses.
 */
function RoleHome() {
  const { user, loading } = useSession();
  if (loading) return <LoadingState />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.isPlatformAdmin) return <Navigate to="/platform/tenants" replace />;

  const first =
    user.role === 'DOCTOR'
      ? '/dashboard/doctor'
      : user.role === 'LAB'
        ? '/lab'
        : user.role === 'RADIOLOGY'
          ? '/imaging'
          : user.role === 'PHARMACY'
            ? '/pharmacy'
            : user.role === 'ACCOUNTING'
              ? '/reports/dashboard'
              : '/dashboard/reception';

  return <Navigate to={first} replace />;
}

export const router = createBrowserRouter([
  {
    element: <BareLayout />,
    children: [
      { path: '/login/*', lazy: async () => ({ Component: (await authRoutes()).AuthLayout }) },
      {
        path: '/forgot-password',
        lazy: async () => ({ Component: (await authRoutes()).ForgotPassword }),
      },
      {
        path: '/reset-password',
        lazy: async () => ({ Component: (await authRoutes()).ResetPassword }),
      },
      {
        // The link every verification email points at. Absent until now, so
        // every one of those links reached the not-found screen.
        path: '/verify-email',
        lazy: async () => ({ Component: (await authRoutes()).VerifyEmail }),
      },
      { path: '/register/*', lazy: async () => ({ Component: (await orgRoutes()).Register }) },
      {
        path: '/book/:slug/*',
        lazy: async () => ({ Component: (await appointmentRoutes()).PublicBooking }),
      },
    ],
  },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <BareLayout />,
        children: [
          {
            path: '/onboarding',
            lazy: async () => ({ Component: (await orgRoutes()).Onboarding }),
          },
          {
            path: '/waiting-room/tv',
            lazy: async () => ({ Component: (await flowRoutes()).TvMode }),
          },
        ],
      },
      {
        element: <ShellLayout />,
        children: [
          { index: true, element: <RoleHome /> },
          {
            path: '/dashboard/*',
            lazy: async () => ({ Component: (await flowRoutes()).Dashboards }),
          },
          { path: '/queue', lazy: async () => ({ Component: (await flowRoutes()).DoctorQueue }) },
          {
            path: '/waiting-room',
            lazy: async () => ({ Component: (await flowRoutes()).WaitingRoom }),
          },
          {
            // EPIC FLOW owns arrival (FLOW-004). The path is the one
            // `navigation.ts` already lists for the `arrival` screen, and it
            // ranks above the `/appointments/*` splat below because a static
            // segment beats a wildcard - so APT keeps the rest of the tree.
            path: '/appointments/:id/arrival',
            lazy: async () => ({ Component: (await flowRoutes()).Arrival }),
          },
          {
            path: '/appointments/*',
            lazy: async () => ({ Component: (await appointmentRoutes()).Appointments }),
          },
          {
            path: '/patients/*',
            lazy: async () => ({ Component: (await patientRoutes()).Patients }),
          },
          { path: '/visits/*', lazy: async () => ({ Component: (await visitRoutes()).Visits }) },
          {
            path: '/follow-ups',
            lazy: async () => ({ Component: (await visitRoutes()).FollowUps }),
          },
          {
            path: '/documents/*',
            lazy: async () => ({ Component: (await visitRoutes()).Documents }),
          },
          { path: '/lab/*', lazy: async () => ({ Component: (await labRoutes()).Lab }) },
          {
            path: '/imaging/*',
            lazy: async () => ({ Component: (await imagingRoutes()).Imaging }),
          },
          {
            path: '/diagnostic-cases/*',
            lazy: async () => ({ Component: (await diagnosticCaseRoutes()).DiagnosticCases }),
          },
          {
            path: '/pharmacy/*',
            lazy: async () => ({ Component: (await pharmacyRoutes()).Pharmacy }),
          },
          {
            path: '/checkout/*',
            lazy: async () => ({ Component: (await financeRoutes()).Checkout }),
          },
          {
            path: '/invoices/*',
            lazy: async () => ({ Component: (await financeRoutes()).Invoices }),
          },
          {
            path: '/payments',
            lazy: async () => ({ Component: (await financeRoutes()).Payments }),
          },
          {
            path: '/expenses',
            lazy: async () => ({ Component: (await financeRoutes()).Expenses }),
          },
          {
            path: '/reports/*',
            lazy: async () => ({ Component: (await financeRoutes()).Reports }),
          },
          { path: '/admin/users', lazy: async () => ({ Component: (await staffRoutes()).Users }) },
          {
            path: '/admin/doctors/*',
            lazy: async () => ({ Component: (await staffRoutes()).Doctors }),
          },
          {
            path: '/admin/branches',
            lazy: async () => ({ Component: (await orgRoutes()).Branches }),
          },
          {
            path: '/admin/services',
            lazy: async () => ({ Component: (await orgRoutes()).Services }),
          },
          {
            path: '/admin/settings/*',
            lazy: async () => ({ Component: (await orgRoutes()).Settings }),
          },
          {
            path: '/admin/subscription',
            lazy: async () => ({ Component: (await orgRoutes()).Subscription }),
          },
          {
            // EPIC REF. `/*` so the feature nests its own sub-routes rather
            // than adding lines here. The ORG placeholder stub it replaced is
            // left in place: removing it would edit another epic's file.
            path: '/admin/reference/*',
            lazy: async () => ({ Component: (await referenceRoutes()).Reference }),
          },
          { path: '/admin/ai', lazy: async () => ({ Component: (await orgRoutes()).AiSettings }) },
          { path: '/admin/audit', lazy: async () => ({ Component: (await orgRoutes()).Audit }) },
          {
            path: '/dev/states',
            lazy: async () => ({ Component: (await devRoutes()).StateGallery }),
          },
        ],
      },
      {
        element: <RequirePlatformAdmin />,
        children: [
          {
            element: <ShellLayout />,
            children: [
              {
                path: '/platform/*',
                lazy: async () => ({ Component: (await platformRoutes()).Platform }),
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: (
      <Suspense fallback={<LoadingState />}>
        <NotFound />
      </Suspense>
    ),
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
